import { useQuery } from "@tanstack/react-query";
import { Wrench } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtDateUk } from "@/lib/utils";
import { DateRangePicker } from "@/components/date-range-picker";
import { Button } from "@/components/ui/button";
import { useDateRange, defaultRange } from "@/lib/use-date-range";

interface OpsSummary {
  sinceDate: string;
  latestSnapshot: Record<string, unknown> | null;
  upcomingWeek: Array<{
    day: string;
    event_type: string;
    events: number;
    total_minutes: number;
  }>;
  engineerLoad: Array<{
    engineer: string;
    events: number;
    total_minutes: number;
    quoted_value: number;
  }>;
}

function fmtInt(n: number | undefined | null): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString();
}
function fmtHrs(mins: number | undefined | null): string {
  if (mins === null || mins === undefined) return "—";
  return `${(mins / 60).toFixed(1)}h`;
}
function fmtGbp(n: number | undefined | null): string {
  if (n === null || n === undefined) return "—";
  return `£${n.toLocaleString()}`;
}

export default function OperationsCentre() {
  const { dateRange, setDateRange, from, to, rangeQs } = useDateRange();
  const { data, isLoading, error } = useQuery<OpsSummary>({
    queryKey: ["ops-summary", from, to],
    queryFn: async () => {
      const res = await fetch(`/api/dashboards/operations${rangeQs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading)
    return (
      <div className="p-6 text-muted-foreground">Loading scheduling data from BigQuery…</div>
    );
  if (error || !data)
    return (
      <div className="p-6 text-red-600">
        Failed to load: {error instanceof Error ? error.message : "unknown error"}
      </div>
    );

  const { latestSnapshot, upcomingWeek, engineerLoad, sinceDate } = data;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Wrench className="h-6 w-6" /> Operations Centre
          </h1>
          <p className="text-sm text-muted-foreground">
            {from && to ? `${from} → ${to}` : `Engineer workload since ${fmtDateUk(sinceDate)} · upcoming 7-day diary`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button variant="ghost" size="sm" onClick={() => setDateRange(defaultRange())}>Reset</Button>
        </div>
      </div>

      {latestSnapshot && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard label="Date" value={String(latestSnapshot.date ?? "—")} />
          <KpiCard label="Total jobs" value={fmtInt(latestSnapshot.total_jobs as number)} />
          <KpiCard label="Scheduled hours" value={String(latestSnapshot.total_hours ?? "—")} />
          <KpiCard label="Active engineers" value={fmtInt(latestSnapshot.active_engineers as number)} />
          <KpiCard
            label="Quoted value"
            value={fmtGbp(Number(latestSnapshot.total_quoted_amount))}
          />
          <KpiCard label="Locked jobs" value={fmtInt(latestSnapshot.locked_jobs as number)} />
          <KpiCard
            label="Estimated miles"
            value={fmtInt(latestSnapshot.estimated_miles as number)}
          />
          <KpiCard
            label="Estimated drive"
            value={String(latestSnapshot.estimated_drive_time ?? "—")}
          />
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Upcoming 7 days — events by type</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Day</th>
                <th className="py-1">Event type</th>
                <th className="text-right">Events</th>
                <th className="text-right">Hours</th>
              </tr>
            </thead>
            <tbody>
              {upcomingWeek.map((e, i) => (
                <tr key={`${e.day}-${e.event_type}-${i}`} className="border-t">
                  <td className="py-1.5 pr-2">{String(e.day)}</td>
                  <td className="py-1.5 pr-2">{e.event_type}</td>
                  <td className="text-right">{fmtInt(e.events)}</td>
                  <td className="text-right">{fmtHrs(e.total_minutes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Engineer workload (last 30 days)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Engineer</th>
                <th className="text-right">Events</th>
                <th className="text-right">Scheduled time</th>
                <th className="text-right">Quoted value</th>
              </tr>
            </thead>
            <tbody>
              {engineerLoad.map((e) => (
                <tr key={e.engineer} className="border-t">
                  <td className="py-1.5 pr-2">{e.engineer}</td>
                  <td className="text-right">{fmtInt(e.events)}</td>
                  <td className="text-right">{fmtHrs(e.total_minutes)}</td>
                  <td className="text-right font-medium">{fmtGbp(e.quoted_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="text-xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}
